/**
 * Notes redesign — per-project + per-author rail colors.
 *
 * The prototype keys per-card visuals off a `hue` value attached to
 * each project / person mock. The real backend has no `color` column
 * on projects or users, so we hash the slug / id into an OKLCH hue.
 *
 * Why OKLCH not HSL: same perceptual lightness across hues, so the
 * `oklch(0.65 0.13 H)` rails read at the same intensity whether H
 * is 18 (orange) or 245 (blue). A future API addition can swap this
 * for a real `color` column without changing the rendering call sites
 * (every chip already calls `projectAccent(slug)`).
 */

/**
 * djb2 hash → 0..360. Stable across page loads (no random) and
 * cheap (no crypto). Empty / null → fallback hue 245 (workspace blue).
 */
const hashTo360 = (s: string | null | undefined): number => {
  if (!s) return 245;
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
};

/**
 * Project rail color — used on `NoteCard.rail`, `NoteRow.rail`, the
 * parent-chip glyph, and the side-panel parent indicator. Lightness +
 * chroma are fixed per the prototype; only hue varies by project.
 */
export const projectAccent = (slug: string | null | undefined): string =>
  `oklch(0.65 0.13 ${hashTo360(slug)})`;

/**
 * Author group glyph color — used in the `NotesGroupHeader` when
 * grouping by author. Slightly desaturated vs project color so the
 * two never read as the same person/project even when their hashes
 * collide.
 */
export const authorAccent = (userId: string | null | undefined): string =>
  `oklch(0.7 0.10 ${hashTo360(userId)})`;
